'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Camera, Check, Loader2, Upload, Users, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { normalizePhoto, PhotoTooLargeError, PHOTO_ACCEPT } from '@/utils/photo-normalize';
import { DetectedFace, MangaPhoto, PhotoAnalysis } from './types';
import { parsePhotoScene, type PhotoScene } from '@/utils/picture-book/photo-scene';
import { trackCreate } from '@/utils/create-flow-analytics';
import type { CreateFlow } from '@/utils/analytics-events';
import { PhotoConsentCheckbox } from '@/components/consent/PhotoConsentCheckbox';
import { createConsentGate, photoConsentJson, withPhotoConsent, type ConsentGate } from '@/components/consent/photo-consent-gate';
import { PHOTO_CONSENT_VERSION } from '@/lib/ethics/photo-consent';

interface MangaPhotoStepProps {
  photo: MangaPhoto | null;
  onPhotoChange: (photo: MangaPhoto | null) => void;
  onNext: () => void;
  /** Which create flow this step belongs to (analytics + copy). */
  flow?: CreateFlow;
  /** A photo to start from (free-cover continue): handled like a parent's upload, with face detection. In memory only; passed as a File, never decoded with fetch (the CSP blocks data: in connect-src). */
  seedPhotoFile?: File;
  /** Called once the seed photo has been taken up, so a remount (after the parent removed it) never re-seeds. */
  onSeedConsumed?: () => void;
  /** A message shown above the upload prompt (e.g. the free-cover photo expired). */
  notice?: string | null;
  /**
   * The free cover behind seedPhotoFile holds a stored consent (R5): no box, and its photo steps inherit it through
   * coverSlug. False: the box is shown and the seed photo waits for the tick, like a fresh upload.
   */
  seedConsented?: boolean;
  /** The free-cover slug (owner proof travels as the cookie), sent with the seed photo's requests so they inherit. */
  coverSlug?: string | null;
}

// The preview route caps the base64 photo at 10 MB.
const MAX_DATA_URL_LENGTH = 10 * 1024 * 1024;
const DETECTION_TIMEOUT_MS = 25000;
// Age/gender estimate (same route as the picture-book wizard). Optional: on timeout we use defaults.
const ANALYSIS_TIMEOUT_MS = 45000;

type DetectionState = 'idle' | 'detecting' | 'analyzing' | 'done' | 'failed';

/** POST the photo to /api/detect-faces with the parent's tick (R3: form field photoConsent). [] unless it succeeds. */
export async function requestFaceDetection(
  file: File,
  consentVersion: string | null,
  fetchImpl: typeof fetch = fetch,
  o: { coverSlug?: string | null; timeoutMs?: number } = {},
): Promise<DetectedFace[]> {
  const timeoutMs = o.timeoutMs ?? DETECTION_TIMEOUT_MS;
  const formData = withPhotoConsent(new FormData(), consentVersion);
  // R5: the free cover's own photo with no tick inherits the cover's stored consent (the server checks ownership)
  if (!consentVersion && o.coverSlug) formData.set('fromCoverSlug', o.coverSlug);
  formData.append('file', file);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetchImpl('/api/detect-faces', { method: 'POST', body: formData, signal: controller.signal });
    const data = await response.json();
    return data?.success && Array.isArray(data.faces) ? (data.faces as DetectedFace[]) : [];
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * What the consent tick shows. A free-cover photo (fromSeed) needs no tick and shows no box (R5); any photo the
 * parent picks needs it, and Continue waits for it.
 */
export function photoConsentUi(s: { photo: MangaPhoto | null; seeding: boolean; seedConsented?: boolean; consented: boolean; blocked: boolean }) {
  // Hidden only for a free-cover photo whose consent the server inherits; a cover without one shows the box
  const seeded = (s.seeding && !!s.seedConsented) || !!s.photo?.fromSeed;
  return {
    showCheckbox: !seeded,
    error: !seeded && s.blocked && !s.consented,
    canContinue: !!s.photo && (!!s.photo.fromSeed || s.consented),
  };
}

/** The analyze-multi-character-photo body: the tick, or (the free cover's photo) the slug the server inherits from. */
export function analyzeFacesBody(
  dataUrl: string,
  faces: DetectedFace[],
  o: { pb: boolean; consent: string | null; coverSlug?: string | null },
): Record<string, unknown> {
  return {
    photoUrl: dataUrl,
    detectedFaces: faces.map((face, index) => ({ index, bbox: face.expanded_bbox || face.bbox })),
    // Picture-book flow: ask the same call for the photo's setting and season (suggestions)
    ...(o.pb ? { scene: true } : {}),
    ...photoConsentJson(o.consent),
    ...(!o.consent && o.coverSlug ? { fromCoverSlug: o.coverSlug } : {}),
  };
}

export function MangaPhotoStep({ photo, onPhotoChange, onNext, flow = 'comic', seedPhotoFile, onSeedConsumed, notice, seedConsented = false, coverSlug = null }: MangaPhotoStepProps) {
  const pb = flow === 'picture-book';
  const [isDragging, setIsDragging] = useState(false);
  const [detectionState, setDetectionState] = useState<DetectionState>(
    photo ? 'done' : 'idle'
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  // A seeded photo is being turned into a photo + faces: show that instead of the upload prompt
  const [seeding, setSeeding] = useState(!!seedPhotoFile && !photo);
  // Bumped per photo so a slow result for an old photo never overwrites a newer one
  const uploadIdRef = useRef(0);
  const { toast } = useToast();
  // The consent tick: unticked by default; kept on the photo so Back then forward keeps it
  const [consent, setConsent] = useState<string | null>(photo?.photoConsentVersion ?? null);
  const consentRef = useRef(consent);
  // A file picked before the tick: held (not uploaded) until the box is ticked
  const [blockedName, setBlockedName] = useState<string | null>(null);
  // The free-cover photo of a cover without stored consent, waiting for the tick
  const [seedWaiting, setSeedWaiting] = useState(false);

  const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Could not read the photo file'));
      reader.readAsDataURL(file);
    });

  const loadImageDimensions = (dataUrl: string): Promise<{ width: number; height: number }> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => reject(new Error('Could not load the photo'));
      img.src = dataUrl;
    });

  const analyzeFaces = async (
    dataUrl: string,
    faces: DetectedFace[],
    consentVersion: string | null,
    fromCover: string | null
  ): Promise<{ analysis: PhotoAnalysis; scene: PhotoScene | null } | undefined> => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), ANALYSIS_TIMEOUT_MS);
    try {
      const response = await fetch('/api/analyze-multi-character-photo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(analyzeFacesBody(dataUrl, faces, { pb, consent: consentVersion, coverSlug: fromCover })),
        signal: controller.signal,
      });
      if (!response.ok) return undefined;
      const { scene, ...analysis } = (await response.json()) as PhotoAnalysis & { scene?: unknown };
      return { analysis: analysis as PhotoAnalysis, scene: pb ? parsePhotoScene(scene) : null };
    } catch (e) {
      console.warn('[MangaPhotoStep] Age/gender analysis failed:', e);
      return undefined;
    } finally {
      clearTimeout(timeout);
    }
  };

  const handleFile = async (picked: File, o: { consent: string | null; fromSeed?: boolean }) => {
    const uploadId = ++uploadIdRef.current;
    // Carried to the preview request (photoConsent) by the payload builders
    const tag = { ...(o.consent ? { photoConsentVersion: o.consent } : {}), ...(o.fromSeed ? { fromSeed: true } : {}) };
    const fromCover = o.fromSeed && !o.consent ? coverSlug : null;
    const isCurrent = () => uploadId === uploadIdRef.current;
    let file = picked;
    if (!file.type.startsWith('image/') && !/\.(heic|heif)$/i.test(file.name)) {
      toast({
        title: 'Not an image',
        description: 'Please upload a PNG or JPG photo.',
        variant: 'destructive',
      });
      return;
    }

    try {
      file = await normalizePhoto(file);
    } catch (e) {
      toast({
        title: e instanceof PhotoTooLargeError ? 'Photo too large' : 'Could not read that photo',
        description:
          e instanceof PhotoTooLargeError
            ? 'Please choose a smaller photo.'
            : 'Please try a different photo (PNG, JPG or WEBP).',
        variant: 'destructive',
      });
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      if (dataUrl.length > MAX_DATA_URL_LENGTH) {
        toast({
          title: 'Photo too large',
          description: 'Please use a smaller photo (under about 7 MB).',
          variant: 'destructive',
        });
        return;
      }
      const dimensions = await loadImageDimensions(dataUrl);
      onPhotoChange({ dataUrl, dimensions, faces: [], ...tag });
      trackCreate(flow, 'photo_uploaded');

      // Face detection is OPTIONAL — if it fails we just proceed without boxes
      setDetectionState('detecting');
      try {
        const faces = await requestFaceDetection(file, o.consent, fetch, { coverSlug: fromCover });
        if (!isCurrent()) return;
        onPhotoChange({ dataUrl, dimensions, faces, ...tag });
        trackCreate(flow, 'faces_detected', { count: faces.length });
        if (faces.length > 0) {
          setDetectionState('analyzing');
          const result = await analyzeFaces(dataUrl, faces, o.consent, fromCover);
          if (!isCurrent()) return;
          if (result) onPhotoChange({ dataUrl, dimensions, faces, analysis: result.analysis, ...(pb ? { scene: result.scene } : {}), ...tag });
        }
        setDetectionState('done');
        if (faces.length > 0) {
          toast({
            title: `Found ${faces.length} ${faces.length === 1 ? 'face' : 'faces'}`,
            description: pb ? 'We pre-filled a card for each face.' : 'We pre-filled a hero card for each face.',
          });
        }
      } catch (detectionError) {
        console.warn('[MangaPhotoStep] Face detection failed:', detectionError);
        if (isCurrent()) setDetectionState('failed');
      }
    } catch (error: any) {
      toast({
        title: 'Upload failed',
        description: error?.message || 'Could not read that photo. Please try another one.',
        variant: 'destructive',
      });
    }
  };

  // Picking or dropping a photo before the tick does not upload it: the file is held and C-04 shown; ticking continues.
  const handleFileRef = useRef(handleFile);
  handleFileRef.current = handleFile;
  const gateRef = useRef<ConsentGate<File> | null>(null);
  if (!gateRef.current) {
    gateRef.current = createConsentGate<File>({
      consent: () => consentRef.current,
      proceed: (file, version) => {
        setBlockedName(null);
        setSeedWaiting(false);
        void handleFileRef.current(file, { consent: version });
      },
      blocked: (file) => setBlockedName(file.name || 'your photo'),
    });
  }
  const pickFile = (file: File) => gateRef.current!.pick(file);

  const handleConsentChange = (checked: boolean) => {
    const version = checked ? PHOTO_CONSENT_VERSION : null;
    consentRef.current = version;
    setConsent(version);
    // The photo already chosen keeps the current answer (unticking stops Continue)
    if (photo && !photo.fromSeed) {
      const { photoConsentVersion: _old, ...rest } = photo;
      onPhotoChange(version ? { ...rest, photoConsentVersion: version } : rest);
    }
    gateRef.current!.consentChanged(version);
  };

  // Seeded photo: run it through the same path as an upload (normalize, size check, face detection, analysis).
  useEffect(() => {
    if (!seedPhotoFile || photo) {
      setSeeding(false);
      return;
    }
    let cancelled = false;
    onSeedConsumed?.();
    (async () => {
      try {
        if (seedConsented || consentRef.current) {
          // The free-cover photo: no tick asked, the server inherits the cover's stored consent (R5)
          await handleFile(seedPhotoFile, { consent: consentRef.current, fromSeed: !consentRef.current });
        } else {
          // A cover without stored consent: the photo waits for the tick, then goes up like a fresh upload
          gateRef.current!.hold(seedPhotoFile);
          setSeedWaiting(true);
        }
      } catch (e) {
        // Falls back to the normal upload prompt; never silent
        console.warn('[cover-seed] could not use the cover photo:', e instanceof Error ? e.message : 'unknown');
      } finally {
        if (!cancelled) setSeeding(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedPhotoFile]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) pickFile(file);
  };

  const handleRemove = () => {
    uploadIdRef.current++;
    gateRef.current!.clear();
    setBlockedName(null);
    setSeedWaiting(false);
    onPhotoChange(null);
    setDetectionState('idle');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const isDetecting = detectionState === 'detecting' || detectionState === 'analyzing';
  const consentUi = photoConsentUi({ photo, seeding, seedConsented, consented: !!consent, blocked: !!blockedName });

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">Upload a Photo</h2>
        <p className="text-gray-600 max-w-xl mx-auto">
          Use one clear, well-lit photo with everyone you want in the {pb ? 'book' : 'comic'} facing the camera.
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={PHOTO_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) pickFile(file);
          // The same file can be picked again (it is held while the box is unticked)
          e.target.value = '';
        }}
      />

      {notice && !photo && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center text-sm text-amber-800" role="status">
          {notice}
        </p>
      )}

      {consentUi.showCheckbox && (
        <div className="space-y-2">
          <PhotoConsentCheckbox checked={!!consent} onChange={handleConsentChange} variant="book" error={consentUi.error} />
          {seedWaiting && !consent && (
            <p className="text-sm text-gray-600" role="status">
              Tick the box to use the photo from your free cover.
            </p>
          )}
          {consentUi.error && blockedName && (
            <p className="text-sm text-gray-600" role="status">
              Photo chosen: <span className="font-medium">{blockedName}</span>. Tick the box to continue.
            </p>
          )}
        </div>
      )}

      {!photo && seeding ? (
        <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-gray-50 p-10 text-gray-600">
          <Loader2 className="w-4 h-4 mr-2 animate-spin text-purple-600" />
          Loading your photo…
        </div>
      ) : !photo ? (
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
            isDragging
              ? 'border-purple-500 bg-purple-50'
              : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
          }`}
        >
          <Camera className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">
            Drag &amp; drop a photo here, or click to choose one
          </p>
          <p className="text-xs text-gray-500 mt-1">PNG, JPG or WEBP</p>
          <Button type="button" className="mt-4 bg-purple-600 hover:bg-purple-700 text-white">
            <Upload className="w-4 h-4 mr-2" />
            Choose Photo
          </Button>
        </div>
      ) : (
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <div className="relative group w-full sm:w-64 shrink-0">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={photo.dataUrl}
                  alt="Uploaded photo"
                  className="w-full rounded-lg border border-gray-200 shadow-sm object-contain max-h-72 bg-white"
                />
                <button
                  type="button"
                  onClick={handleRemove}
                  className="absolute -top-2 -right-2 bg-white border border-gray-200 hover:bg-red-50 hover:border-red-300 text-gray-600 hover:text-red-600 rounded-full p-1.5 shadow-sm transition-all"
                  title="Remove photo"
                  aria-label="Remove photo"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="flex-1 space-y-3">
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-green-600" />
                  </div>
                  <span className="text-green-700 font-medium">Photo ready</span>
                  <span className="text-gray-400 text-xs">
                    {photo.dimensions.width} × {photo.dimensions.height}px
                  </span>
                </div>

                {isDetecting ? (
                  <div className="flex items-center text-sm text-gray-600">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin text-purple-600" />
                    {detectionState === 'analyzing'
                      ? (pb ? 'Getting to know everyone...' : 'Getting to know your heroes...')
                      : 'Looking for faces...'}
                  </div>
                ) : detectionState === 'failed' ? (
                  <p className="text-sm text-amber-600">
                    Face detection is unavailable right now — no problem, you can still continue
                    and describe {pb ? 'everyone' : 'your heroes'} on the next step.
                  </p>
                ) : (
                  <div className="flex items-center text-sm text-gray-700">
                    <Users className="w-4 h-4 mr-2 text-purple-600" />
                    {photo.faces.length > 0
                      ? `${photo.faces.length} ${photo.faces.length === 1 ? 'face' : 'faces'} detected — we'll pre-fill your ${pb ? 'cards' : 'hero cards'}`
                      : 'No faces detected — you can still add ' + (pb ? 'everyone' : 'your heroes') + ' manually'}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  Change photo
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button
          size="lg"
          className="bg-purple-600 hover:bg-purple-700 text-white w-full sm:w-auto"
          disabled={!consentUi.canContinue || isDetecting}
          onClick={onNext}
        >
          {isDetecting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Finding faces...
            </>
          ) : (
            pb ? 'Continue' : 'Continue to Heroes'
          )}
        </Button>
      </div>
    </div>
  );
}
